import {
  mdiAlert,
  mdiClipboardAlertOutline,
  mdiClipboardCheckOutline,
  mdiClipboardText,
  mdiClockOutline,
  mdiInformationOutline,
  mdiUndo
} from '@mdi/js';
import type { NotificationToRender, NotificationType, SignalRNotification } from 'common/models';
import { Icon } from 'components';
import React from 'react';
import type { NavigateFunction } from 'react-router-dom';

// Reasons the backend sends in UserDeviceInvalidated.data.Reason. Each one has a matching
// `user_device_invalidated_<Reason>` translation key in the `common` scope.
export const DEVICE_INVALIDATION_REASONS = ['NewLogin', 'PasswordChanged', 'AccessRevoked'] as const;

export type DeviceInvalidationReason = (typeof DEVICE_INVALIDATION_REASONS)[number];

/**
 * Decides whether a UserDeviceInvalidated notification means *this* device was logged out by the
 * single-device policy, and why.
 *
 * Returns the reason to show the user, or null when the notification is about someone else's
 * device (or isn't a device-invalidation at all) and should be ignored. An unknown/missing reason
 * falls back to 'NewLogin' — the by-far most common case — so a future backend value still
 * produces a sensible message rather than a blank one.
 */
export const getDeviceInvalidationReason = (
  notification: SignalRNotification,
  currentDeviceId: string
): DeviceInvalidationReason | null => {
  if (notification.type !== 'UserDeviceInvalidated') return null;

  const invalidatedIds =
    (notification.data.InvalidatedDeviceIdentifiers as string[] | undefined) ?? [];

  if (!invalidatedIds.includes(currentDeviceId)) return null;

  const rawReason = String(notification.data.Reason ?? '');

  return DEVICE_INVALIDATION_REASONS.find((reason) => reason === rawReason) ?? 'NewLogin';
};

export const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'Warning':
      return <Icon path={mdiAlert} className="icon-warning" />;
    case 'InterventionWaitingToConfirm':
      return <Icon path={mdiClockOutline} className="icon-access" />;
    case 'InterventionConfirmed':
      return <Icon path={mdiClipboardCheckOutline} className="icon-turnedin" />;
    case 'InterventionDenied':
      return <Icon path={mdiClipboardAlertOutline} className="icon-warning" />;
    case 'InterventionChanged':
      return <Icon path={mdiClipboardText} className="icon-changed" />;
    case 'InterventionAssigned':
      return <Icon path={mdiClipboardText} className="icon-assigned" />;
    case 'InterventionUnassigned':
      return <Icon path={mdiUndo} className="icon-undo" />;
    default:
      return <Icon path={mdiInformationOutline} className="icon-info" />;
  }
};

export const getNotificationLink = (
  notification: NotificationToRender,
  navigate: NavigateFunction
) => {
  if (notification.type.includes('Intervention')) {
    return navigate(
      `/orders/${notification.data?.OrderId}/interventions/${notification.data?.InterventionId}`
    );
  }

  if (notification.type.includes('OrderRequest')) {
    return navigate(`/request/order/${notification.data?.OrderRequestId}`);
  }

  if (notification.type.includes('CustomerPortalRequest')) {
    return navigate(`/request/fromPortal/${notification.data?.CustomerPortalRequestId}`);
  }

  if (notification.type.includes('Order')) {
    return navigate(`/orders/${notification.data?.OrderId}`);
  }

  return navigate(`/supportTickets/${notification.data?.SupportTicketId}`);
};

export const isNotificationLinkActive = (type: NotificationType) =>
  (type.includes('Intervention') ||
    type.includes('SupportTicket') ||
    type.includes('OrderRequestAdded') ||
    type.includes('CustomerPortalRequest') ||
    type.includes('OrderEditedByCustomer')) &&
  type !== 'InterventionUnassigned';
